import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Upload, FileText, Download, AlertCircle, CheckCircle, Info, FileDown } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { fileCompareAPI } from '../services/api'

interface FileUpload {
  file: File
  type: 'ITC' | 'GSTR2B'
  progress: number
  status: 'uploading' | 'completed' | 'error'
  fileName?: string
}

export default function FileCompare() {
  const [uploads, setUploads] = useState<FileUpload[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)

  const downloadTemplate = async (templateName: string) => {
    try {
      const response = await fileCompareAPI.downloadTemplate(templateName)
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${templateName}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success(`${templateName} downloaded successfully!`)
    } catch (error) {
      toast.error(`Failed to download ${templateName}`)
      console.error('Download error:', error)
    }
  }

  const onDrop = async (acceptedFiles: File[], type: 'ITC' | 'GSTR2B') => {
    const newUploads = acceptedFiles.map(file => ({
      file,
      type,
      progress: 0,
      status: 'uploading' as const
    }))
    
    setUploads(prev => [...prev, ...newUploads])
    
    // Upload each file
    for (const upload of newUploads) {
      try {
        const uploadFunction = type === 'ITC' ? fileCompareAPI.uploadITCFile : fileCompareAPI.uploadGSTR2BFile
        const response = await uploadFunction(upload.file)
        
        setUploads(prev => prev.map(u => {
          if (u.file === upload.file) {
            return { 
              ...u, 
              progress: 100, 
              status: 'completed',
              // Prefer the server-saved name; fallback to reported fileName
              fileName: response.data.savedFileName || response.data.fileName
            }
          }
          return u
        }))
        
        toast.success(`${type} file uploaded successfully!`)
      } catch (error) {
        setUploads(prev => prev.map(u => {
          if (u.file === upload.file) {
            return { ...u, status: 'error' }
          }
          return u
        }))
        
        toast.error(`Failed to upload ${type} file`)
        console.error('Upload error:', error)
      }
    }
  }

  const { getRootProps: getITCRootProps, getInputProps: getITCInputProps, isDragActive: isITCDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, 'ITC'),
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false
  })

  const { getRootProps: getGSTR2BRootProps, getInputProps: getGSTR2BInputProps, isDragActive: isGSTR2BDragActive } = useDropzone({
    onDrop: (files) => onDrop(files, 'GSTR2B'),
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    multiple: false
  })

  const handleCompare = async () => {
    const itcFile = uploads.find(u => u.type === 'ITC' && u.status === 'completed')
    const gstr2bFile = uploads.find(u => u.type === 'GSTR2B' && u.status === 'completed')

    if (!itcFile || !gstr2bFile) {
      toast.error('Please upload both ITC and GSTR2B files')
      return
    }

    setIsProcessing(true)
    
    try {
      const response = await fileCompareAPI.compareFiles({
        itcFile: itcFile.fileName || itcFile.file.name,
        gstr2bFile: gstr2bFile.fileName || gstr2bFile.file.name
      })
      
      const success = response?.data?.success
      const newJobId = response?.data?.jobId

      if (!success || !newJobId) {
        const msg = response?.data?.message || 'Comparison could not be started. Please verify files.'
        toast.error(msg)
        setIsProcessing(false)
        return
      }

      setJobId(newJobId)
      toast.success('File comparison started!')

      // Poll job status until completed or failed
      const pollIntervalMs = 2000
      const poll = async () => {
        try {
          // Guard against undefined jobId or cleared state
          if (!newJobId) {
            setIsProcessing(false)
            return
          }
          const statusRes = await fileCompareAPI.getStatus(newJobId)
          const status = statusRes.data?.status || statusRes.data?.data?.status
          if (status === 'completed') {
            setIsProcessing(false)
            toast.success('Comparison completed. You can download the report.')
            return
          }
          if (status === 'failed') {
            setIsProcessing(false)
            toast.error(statusRes.data?.message || 'Comparison failed')
            return
          }
          // keep polling if processing
          setTimeout(poll, pollIntervalMs)
        } catch (e) {
          // On transient error, keep polling a few times
          setTimeout(poll, pollIntervalMs)
        }
      }
      setTimeout(poll, pollIntervalMs)
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to start comparison'
      if (errorMessage.includes('Header validation failed')) {
        toast.error('File headers do not match the expected template format. Please download and use the provided templates.')
      } else {
        toast.error(errorMessage)
      }
      console.error('Comparison error:', error)
      setIsProcessing(false)
    } finally {
      // If polling started, it will flip isProcessing. If not, we've already reset it.
    }
  }

  const handleDownload = async () => {
    if (!jobId) return
    
    try {
      const response = await fileCompareAPI.downloadReport(jobId)
      
      // Create blob and download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `comparison_report_${jobId}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Report downloaded successfully!')
    } catch (error) {
      toast.error('Failed to download report')
      console.error('Download error:', error)
    }
  }

  const clearUploads = () => {
    setUploads([])
    setJobId(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">GST ITC Reconciliation Tool</h1>
        <p className="text-gray-600 mt-2">
          Upload your ITC register and GSTR2B files to compare and identify discrepancies.
        </p>
      </div>

      {/* Instructions Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <Info className="h-6 w-6 text-blue-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">Instructions</h2>
        </div>
        
        <div className="space-y-6">
          {/* Step 1: Download Templates */}
          <div className="border-l-4 border-blue-500 pl-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">1️⃣ Download Templates</h3>
            <p className="text-gray-600 mb-4">Download the required templates to ensure proper data format:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => downloadTemplate('ITC Register')}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <FileDown className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">ITC Register Template</span>
              </button>
              <button
                onClick={() => downloadTemplate('GSTR2B')}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <FileDown className="h-5 w-5 text-blue-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">GSTR2B Template</span>
              </button>
            </div>
          </div>

          {/* Step 2: Fill Data */}
          <div className="border-l-4 border-green-500 pl-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">2️⃣ Fill Data</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <ul className="text-sm text-gray-700 space-y-2">
                <li>• Enter details in the same column headers provided.</li>
                <li>• Do not delete or rename any columns.</li>
                <li>• Ensure all required fields are filled.</li>
                <li>• <strong>Important:</strong> Column headers must exactly match the template format for validation.</li>
              </ul>
            </div>
          </div>

          {/* Step 3: Upload Files */}
          <div className="border-l-4 border-yellow-500 pl-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">3️⃣ Upload Files</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                Upload your completed ITC Register and GSTR2B files using the upload sections below.
              </p>
            </div>
          </div>

          {/* Step 4: Run Comparison */}
          <div className="border-l-4 border-purple-500 pl-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">4️⃣ Run Comparison</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                Click "Reconcile Now" to process your data and generate comparison results.
              </p>
            </div>
          </div>

          {/* Step 5: Download Report */}
          <div className="border-l-4 border-red-500 pl-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">5️⃣ Download Report</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                Get a detailed report showing matched and unmatched invoices with match type details.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ITC Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">ITC Register</h3>
          <div
            {...getITCRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isITCDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary'
            }`}
          >
            <input {...getITCInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {isITCDragActive ? 'Drop the ITC file here' : 'Drag & drop ITC file here, or click to select'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        </div>

        {/* GSTR2B Upload */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">GSTR2B</h3>
          <div
            {...getGSTR2BRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isGSTR2BDragActive ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-primary'
            }`}
          >
            <input {...getGSTR2BInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              {isGSTR2BDragActive ? 'Drop the GSTR2B file here' : 'Drag & drop GSTR2B file here, or click to select'}
            </p>
            <p className="text-xs text-gray-500 mt-1">Supports .xlsx, .xls, .csv</p>
          </div>
        </div>
      </div>

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Progress</h3>
          <div className="space-y-4">
            {uploads.map((upload, index) => (
              <div key={index} className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900">
                      {upload.file.name} ({upload.type})
                    </p>
                    <span className="text-sm text-gray-500">{upload.progress}%</span>
                  </div>
                  <div className="mt-2 bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        upload.status === 'completed' ? 'bg-green-500' : 
                        upload.status === 'error' ? 'bg-red-500' : 'bg-primary'
                      }`}
                      style={{ width: `${upload.progress}%` }}
                    />
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {upload.status === 'completed' && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                  {upload.status === 'error' && (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Actions</h3>
            <p className="text-sm text-gray-600 mt-1">
              {uploads.length > 0 
                ? `${uploads.filter(u => u.status === 'completed').length} files ready for comparison`
                : 'Upload files to start comparison'
              }
            </p>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={clearUploads}
              className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={uploads.length === 0}
            >
              Clear All
            </button>
            <button
              onClick={handleCompare}
              disabled={uploads.filter(u => u.status === 'completed').length < 2 || isProcessing}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing...' : 'Reconcile Now'}
            </button>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {jobId && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Comparison Results</h3>
              <p className="text-sm text-gray-600 mt-1">Job ID: {jobId}</p>
              {isProcessing ? (
                <p className="text-xs text-blue-600 mt-1 flex items-center">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-1"></div>
                  Processing files... Please wait
                </p>
              ) : (
                <p className="text-xs text-green-600 mt-1 flex items-center">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Processing completed successfully
                </p>
              )}
            </div>
            <button
              onClick={handleDownload}
              disabled={isProcessing}
              className={`inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 shadow-sm ${
                isProcessing 
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 hover:shadow-md transform hover:scale-105'
              }`}
            >
              <FileDown className="h-4 w-4 mr-2" />
              {isProcessing ? 'Processing...' : 'Download Results'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
} 
